import React, { useState } from "react";
import { Card, Flex, Text, Button, Badge } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import { AuthComoDialog } from "./AuthComoDialog";
import { PermissionAlertDialog } from "components";
import { useQueryClient } from "@tanstack/react-query";
import { useValidateComo, useGetComoStatus } from "hooks";
import { ComoChoiceDialog } from "./ComoChoiceDialog";
import { ComoGPSWarningDialog } from "./ComoGPSWarningDialog";
import comoLogo from "../../../assets/images/como.svg";

export function BrowseComoCard({ userProfile }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState("");
  const [isApiKeyValid, setIsApiKeyValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [choiceDialogOpen, setChoiceDialogOpen] = useState(false);
  const [pendingChoiceData, setPendingChoiceData] = useState(null);
  const [primaryIdentifier, setPrimaryIdentifier] = useState("email");
  const [identifierError, setIdentifierError] = useState(false);
  const [gpsDialogOpen, setGpsDialogOpen] = useState(false);
  const [gpsData, setGpsData] = useState(null);
  
  const validateComo = useValidateComo();
  const { data: comoStatusData, isLoading: isStatusLoading } = useGetComoStatus(userProfile?.organisation_id);

  const isComoActive = () => {
    return comoStatusData?.data?.is_active === true;
  };

  const handleOpenChange = (isOpen) => {
    setOpenDialog(isOpen);
    if (!isOpen) handleCancelAuthDialog();
  };

  const handleConnectClick = () => {
    const isOwner = userProfile?.user_role === "Owner";
    if (isOwner) handleOpenChange(true);
    else setShowPermissionAlert(true);
  };

  const handleCancelAuthDialog = () => {
    setApiKey("");
    setIsApiKeyValid(true);
  };

  const handleApiKeyChange = (event) => {
    setApiKey(event.target.value);
    setIsApiKeyValid(true);
  };

  const handleSave = async (choice = null) => {
    if (!apiKey) {
      setIsApiKeyValid(false);
      return;
    }

    if (!primaryIdentifier) {
      setIdentifierError(true);
      return;
    }

    setIsLoading(true);
    setIdentifierError(false);

    try {
      const payload = {
        api_key: apiKey,
        organisation_id: userProfile.organisation_id,
        primary_identifier: primaryIdentifier,
      };

    if (choice) payload.action_choice = choice;

    const result = await validateComo.mutateAsync(payload);
    console.log("RESULT:", result)
    const status = result?.data?.status;

    if (status === "manual_gps_update_required") {
        setGpsData(result.data);
        setGpsDialogOpen(true);
        return;
      }

    if (status === "pending_user_choice") {
      setPendingChoiceData(result.data);
      setChoiceDialogOpen(true);
      return;
    }

    if (status === "success" || status === "restored" || status === "resumed") {
      queryClient.invalidateQueries(["como-status"]);
      navigate("/activation-success", {
        state: {
          description: "Your customers will now sync with your Como account.",
          integrationType: "Como",
        },
      });
      return;
    }

    // Only treat these as invalid
    if (status === "invalid" || status === "error" || result?.data?.invalid === "api_key") {
      setIsApiKeyValid(false);
    }
  } catch (error) {
    console.error("Validation error:", error);
    const invalidField = error?.response?.data?.invalid;
    const status = error?.response?.status;
    if (invalidField === "api_key" || status === 401 || status === 403 || status === 500) {
      setIsApiKeyValid(false);
    }
  } finally {
    setIsLoading(false);
  }
};

  return (
    <>
      <Card
        style={{
          display: "flex",
          width: "100%",
          minWidth: 0,
          padding: "var(--space-4)",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "var(--space-3)",
          overflow: "hidden",
        }}
      >
        {/* Como integration name and logo */}
        <Flex align="center" gap="4" style={{ minWidth: 0, flex: "1 1 auto" }}>
          <Flex
            height="40px"
            width="40px"
            p="2"
            style={{
              flexShrink: 0,
              borderRadius: "var(--space-1)",
              background: "#FFFFFF",
              border: "1px solid var(--gray-a6)",
            }}
          >
            <img
              src={comoLogo}
              alt="como"
              style={{ width: "24px" }}
            />
          </Flex>
          <Flex direction="column" justify="center" align="start" style={{ minWidth: 0 }}>
            <Text size="4" weight="medium" truncate>Como</Text>
            <Text size="1" color="gray" truncate>CRM & Loyalty platform</Text>
          </Flex>
        </Flex>

        {/* Como integration status and action button */}
        <Flex align="center" style={{ flexShrink: 0 }}>
          {isComoActive() ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : (
            <Button
              onClick={handleConnectClick}
              size="2"
              variant="soft"
              color="gray"
            >
              Connect
            </Button>
          )}
        </Flex>
      </Card>

      <AuthComoDialog
        isOpen={openDialog}
        onOpenChange={handleOpenChange}
        onCancel={handleCancelAuthDialog}
        onSave={handleSave}
        handleApiKeyChange={handleApiKeyChange}
        isApiKeyValid={isApiKeyValid}
        isLoading={isLoading}
        progressValue={progressValue}
        primaryIdentifier={primaryIdentifier}
        setPrimaryIdentifier={setPrimaryIdentifier}
        identifierError={identifierError}
      />

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />

      {/* Resume/continue/historical sync dialog */}
      <ComoChoiceDialog
        isOpen={choiceDialogOpen}
        onOpenChange={setChoiceDialogOpen}
        data={pendingChoiceData}
        onChoose={(choice) => {
          setChoiceDialogOpen(false);
          handleSave(choice);
        }}
      />

      <ComoGPSWarningDialog
        isOpen={gpsDialogOpen}
        onOpenChange={setGpsDialogOpen}
        data={gpsData}
      />
    </>
  );
}
